import type { RecurrenceRule } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";
import { ackedColumn, clampAck, type SyncDestination } from "./mut.js";

const RULE_CONTENT_SET = `rrule=?,dtstart=?,until=?,tz=?,exdates=?,updated_at=?,deleted_at=?`;
const RULE_UPDATE_SQL = `UPDATE recurrence_rules SET ${RULE_CONTENT_SET},mut=mut+1 WHERE id=?`;
const RULE_UPDATE_SYNCED_SQL: Record<SyncDestination, string> = {
  server: `UPDATE recurrence_rules SET ${RULE_CONTENT_SET},mut=mut+1,server_acked_mut=mut+1 WHERE id=?`,
  google: `UPDATE recurrence_rules SET ${RULE_CONTENT_SET},mut=mut+1,google_acked_mut=mut+1 WHERE id=?`,
};

export class RecurrenceRepository {
  constructor(private readonly db: StorageAdapter) {}

  /** `synced` marks the row as already in step with that destination (an inbound
   *  sync/Google apply); a plain insert starts dirty for both. */
  async insert(r: RecurrenceRule, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      `INSERT INTO recurrence_rules (id,rrule,dtstart,until,tz,exdates,mut,server_acked_mut,google_acked_mut,created_at,updated_at,deleted_at) VALUES (?,?,?,?,?,?,?,?,?,?,?,?)`,
      [
        r.id, r.rrule, r.dtstart, r.until, r.tz ?? null,
        JSON.stringify(r.exdates ?? []),
        1, synced === "server" ? 1 : 0, synced === "google" ? 1 : 0,
        r.created_at, r.updated_at, r.deleted_at,
      ],
    );
  }

  /** Every content write bumps `mut` (the sync engines' per-row dirty fact);
   *  `synced` self-acknowledges a write whose content came FROM that destination.
   *  The incoming object's own mut counters are ignored — the chokepoint owns them. */
  async update(r: RecurrenceRule, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      synced ? RULE_UPDATE_SYNCED_SQL[synced] : RULE_UPDATE_SQL,
      [
        r.rrule, r.dtstart, r.until, r.tz ?? null,
        JSON.stringify(r.exdates ?? []),
        r.updated_at, r.deleted_at, r.id,
      ],
    );
  }

  /** Clamped acknowledgement — see `TasksRepository.ackMut`. */
  async ackMut(id: string, dest: SyncDestination, upTo: number): Promise<void> {
    const current = await this.findById(id);
    if (!current) return;
    const next = clampAck(current, dest, upTo);
    if (next === null) return;
    await this.db.execute(`UPDATE recurrence_rules SET ${ackedColumn(dest)}=? WHERE id=?`, [next, id]);
  }

  async findById(id: string): Promise<RecurrenceRule | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM recurrence_rules WHERE id=?`, [id],
    );
    const r = rows[0];
    return r ? fromRow(r) : null;
  }

  async listActive(): Promise<RecurrenceRule[]> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM recurrence_rules WHERE deleted_at IS NULL`,
    );
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync. */
  async listAll(): Promise<RecurrenceRule[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM recurrence_rules`);
    return rows.map(fromRow);
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM recurrence_rules WHERE id=?`, [id]);
  }
}

function fromRow(r: Record<string, unknown>): RecurrenceRule {
  return {
    id: String(r["id"]),
    rrule: String(r["rrule"]),
    dtstart: String(r["dtstart"]),
    until: (r["until"] as string | null) ?? null,
    tz: (r["tz"] as string | null) ?? null,
    exdates: parseExdates(r["exdates"]),
    mut: Number(r["mut"] ?? 0),
    server_acked_mut: Number(r["server_acked_mut"] ?? 0),
    google_acked_mut: Number(r["google_acked_mut"] ?? 0),
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}

function parseExdates(v: unknown): string[] {
  if (typeof v !== "string" || v.length === 0) return [];
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
